import { Icon } from '@acs/ui';
import { LOCALE_ENDONYM, UI_LOCALES, type UiLocale } from './locales.js';
import { useI18n } from './useI18n.js';

/**
 * The ONE portal-language picker: the landing bar, the auth top bar and the
 * account menu (app shell and setup wizard) all render this, so the control
 * looks and behaves the same everywhere.
 *
 * The choice is stored on the device by `I18nProvider`, so it is also the
 * language a new account starts in: the verification email goes out in it, and
 * the first business's assistant language defaults to it when the assistant
 * speaks that language (`CreateBusinessPage`).
 *
 * The closed control shows only the short code ("PL"): on a phone it shares a
 * bar with the full "TopCall24" wordmark, and a full endonym ("Українська")
 * pushed the wordmark out. The open list still names each language in its
 * OWN language ("PL · Polski"): a visitor who landed in a language they cannot
 * read is scanning for the word they would write themselves, and the list
 * costs no room in the bar.
 *
 * The face is drawn by us; a transparent native <select> lies over it, so the
 * keyboard, screen readers and the phone's own picker keep working.
 */
export function LanguageSelect({ className, icon = true }: {
  className?: string;
  /** The globe. Off where a label beside it already carries one (account menu). */
  icon?: boolean;
}) {
  const { t, locale, setLocale } = useI18n();
  return (
    <label className={className ? `lang-select ${className}` : 'lang-select'}>
      {icon && <Icon name="globe" size={18} />}
      <span className="lang-select__code" aria-hidden>{locale.toUpperCase()}</span>
      <Icon name="chevron-down" size={16} className="lang-select__chevron" />
      <select
        className="lang-select__native"
        aria-label={t('account.language')}
        value={locale}
        onChange={(e) => setLocale(e.target.value as UiLocale)}
      >
        {UI_LOCALES.map((code) => (
          <option key={code} value={code} lang={code}>
            {`${code.toUpperCase()} · ${LOCALE_ENDONYM[code]}`}
          </option>
        ))}
      </select>
    </label>
  );
}
